import React, { useEffect, useState } from 'react';
import axios from 'axios';
import { Loader2 } from 'lucide-react';
import { LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer } from 'recharts';

type CostsGraphProps = {
  dateFrom: string;
  dateTo: string;
  selectedCountries: string[];
  selectedCampaigns: string[];
  selectedPlatforms: string[];
  viewMode: 'daily' | 'monthly' | 'weekly';
  showWithTax: boolean;
  vatRates: { [country: string]: number };
};

const CostsGraphComponent: React.FC<CostsGraphProps> = ({
  dateFrom,
  dateTo,
  selectedCountries,
  selectedCampaigns,
  selectedPlatforms,
  viewMode,
  showWithTax,
  vatRates,
}) => {
  const [loading, setLoading] = useState<boolean>(true);
  const [error, setError] = useState<string | null>(null);
  const [chartData, setChartData] = useState<any[]>([]);

  // Function to get adjusted revenue with VAT
  const getAdjustedRevenue = (value: number, country: string): number => {
    if (value === null || value === undefined) return 0;
    if (!showWithTax) return value;
    
    const vatRate = vatRates[country] !== undefined ? vatRates[country] : 0.0;
    return value * (1 - vatRate);
  };

  // Function to calculate total revenue like in main component
  const calculateTotalRevenue = (item: any, country: string): number => {
    // Start with revenue_activation, applying VAT
    let total = getAdjustedRevenue(item.revenue_activation || 0, country);

    // Add all actual revenues for periods P1-P12, applying VAT to each
    for (let i = 1; i <= 12; i++) {
      const key = `revenue_p${i}`;
      const value = item[key];
      
      // Check that value exists and is a number
      if (typeof value === 'number') {
        total += getAdjustedRevenue(value, country);
      }
    }
    
    return total;
  };

  useEffect(() => {
    const fetchGraphData = async () => {
      setLoading(true);
      try {
        // Same endpoint as main component
        let endpoint = 'https://dashboard-nk.golive.mobi/api/web/api/orders';
        const params: Record<string, any> = {};
        
        // Choose correct keyevent based on viewMode
        if (viewMode === 'daily') {
          params.keyevent = 'days';
        } else if (viewMode === 'weekly') {
          params.keyevent = 'weeks';
        } else if (viewMode === 'monthly') {
          params.keyevent = 'month';
        }
        
        if (selectedCountries.length > 0) {
          params.country_filter = selectedCountries.join(',');
        }
        
        if (selectedCampaigns.length > 0) {
          params.campaign = selectedCampaigns.join(',');
        }
        
        // Date params
        if (dateFrom) params.date_from = dateFrom;
        if (dateTo) params.date_to = dateTo;
        
        const response = await axios.get(endpoint, { params });
        const data = response.data.data;
        
        // Process data for Recharts
        const formattedData: any[] = [];
        
        // Sort dates for correct graph timeline
        const sortedDates = Object.keys(data).sort();
        
        sortedDates.forEach(date => {
          // Format date for display based on viewMode
          let formattedDate;
          
          if (viewMode === 'monthly') {
            // For monthly view
            formattedDate = new Date(date + '-01').toLocaleDateString('default', { month: 'short', year: 'numeric' });
          } else if (viewMode === 'weekly') {
            // For weekly view - check date format
            if (date.includes(' - ')) {
              // This is a date range like "2023-01-01 - 2023-01-07"
              const [start, end] = date.split(' - ');
              formattedDate = `${new Date(start).toLocaleDateString('default', { day: 'numeric', month: 'short' })} - ${new Date(end).toLocaleDateString('default', { day: 'numeric', month: 'short' })}`;
            } else {
              // Single date
              formattedDate = new Date(date).toLocaleDateString('default', { day: 'numeric', month: 'short', year: 'numeric' });
            }
          } else {
            // For daily view
            formattedDate = new Date(date).toLocaleDateString();
          }
          
          // Initialize aggregates for this date
          let totalCosts = 0;
          let totalRevenue = 0;
          
          // Loop through each country and campaign
          Object.entries(data[date]).forEach(([country, countryItems]: [string, any]) => {
            // Filter by selected countries if needed
            if (selectedCountries.length === 0 || selectedCountries.includes(country)) {
              countryItems.forEach((item: any) => {
                // Filter by selected campaigns if needed
                const campaignMatch = selectedCampaigns.length === 0 || selectedCampaigns.includes(item.campaign);
                const platformMatch = selectedPlatforms.length === 0 || selectedPlatforms.includes(item.platform || '---');
                
                if (campaignMatch && platformMatch) {
                  // Accumulate costs
                  totalCosts += item.costs || 0;
                  
                  // Calculate and accumulate revenue with VAT adjustment if needed
                  const calculatedRevenue = calculateTotalRevenue(item, country);
                  totalRevenue += calculatedRevenue;
                }
              });
            }
          });
          
          // Calculate ROI as a percentage
          const roi = totalCosts > 0 ? ((totalRevenue - totalCosts) / totalCosts) * 100 : 0;
          
          // Add data point
          formattedData.push({
            date: formattedDate,
            costs: totalCosts,
            revenue: totalRevenue,
            roi: roi
          });
        });
        
        setChartData(formattedData);
        setLoading(false);
      } catch (err) {
        console.error('Error fetching graph data:', err);
        setError('Failed to fetch graph data');
        setLoading(false);
      }
    };
    
    fetchGraphData();
  }, [dateFrom, dateTo, selectedCountries, selectedCampaigns, selectedPlatforms, viewMode, showWithTax, vatRates]);

  if (loading) {
    return (
      <div style={{
        backgroundColor: 'white',
        borderRadius: '12px',
        margin: '20px 30px',
        padding: '20px',
        boxShadow: '0 4px 10px rgba(0,0,0,0.06)',
        height: '400px',
        display: 'flex',
        justifyContent: 'center',
        alignItems: 'center'
      }}>
        <Loader2 
          size={48} 
          style={{ 
            animation: 'spin 1s linear infinite',
            color: '#3498db'
          }} 
        />
      </div>
    );
  }

  if (error) {
    return (
      <div style={{ 
        backgroundColor: 'white',
        borderRadius: '12px',
        margin: '20px 30px',
        padding: '20px',
        boxShadow: '0 4px 10px rgba(0,0,0,0.06)',
        color: 'red'
      }}>
        {error}
      </div>
    );
  }

  // Get appropriate title for X axis based on viewMode
  const getXAxisLabel = () => {
    switch (viewMode) {
      case 'daily':
        return 'Date';
      case 'weekly':
        return 'Week Range';
      case 'monthly':
        return 'Month';
      default:
        return 'Period';
    }
  };

  return (
    <div style={{ 
      backgroundColor: 'white',
      borderRadius: '12px',
      margin: '20px 30px',
      padding: '20px',
      boxShadow: '0 4px 10px rgba(0,0,0,0.06)'
    }}>
      {/* Costs Chart */}
      <div style={{ marginBottom: '30px' }}>
        <h2 style={{ 
          margin: '0 0 20px 0', 
          fontSize: '18px',
          fontWeight: 600,
          color: '#2c3e50'
        }}>
          Costs Over Time
        </h2>
        
        <div style={{ width: '100%', height: 300 }}>
          <ResponsiveContainer>
            <LineChart
              data={chartData}
              margin={{ top: 20, right: 30, left: 20, bottom: 60 }}
            >
              <CartesianGrid strokeDasharray="3 3" />
              <XAxis 
                dataKey="date" 
                label={{ 
                  value: getXAxisLabel(), 
                  position: 'insideBottom', 
                  offset: -40 
                }}
                angle={-45}
                textAnchor="end"
                height={60}
              />
              <YAxis 
                label={{ 
                  value: 'Costs', 
                  angle: -90, 
                  position: 'insideLeft',
                  style: { textAnchor: 'middle' }
                }}
              />
              <Tooltip formatter={(value) => typeof value === 'number' ? value.toFixed(2) : value} />
              <Legend verticalAlign="top" height={36} />
              <Line 
                type="monotone" 
                dataKey="costs" 
                name="Costs" 
                stroke="#e74c3c" 
                activeDot={{ r: 8 }} 
                strokeWidth={2}
              />
            </LineChart>
          </ResponsiveContainer>
        </div>
      </div>
      
      {/* Revenue Chart */}
      <div style={{ marginBottom: '30px' }}>
        <h2 style={{ 
          margin: '0 0 20px 0', 
          fontSize: '18px',
          fontWeight: 600,
          color: '#2c3e50'
        }}>
          Revenue Over Time {showWithTax ? '(After VAT)' : ''}
        </h2>
        
        <div style={{ width: '100%', height: 300 }}>
          <ResponsiveContainer>
            <LineChart
              data={chartData}
              margin={{ top: 20, right: 30, left: 20, bottom: 60 }}
            >
              <CartesianGrid strokeDasharray="3 3" />
              <XAxis 
                dataKey="date" 
                label={{ 
                  value: getXAxisLabel(), 
                  position: 'insideBottom', 
                  offset: -40 
                }}
                angle={-45}
                textAnchor="end"
                height={60}
              />
              <YAxis 
                label={{ 
                  value: 'Revenue', 
                  angle: -90, 
                  position: 'insideLeft',
                  style: { textAnchor: 'middle' }
                }}
              />
              <Tooltip formatter={(value) => typeof value === 'number' ? value.toFixed(2) : value} />
              <Legend verticalAlign="top" height={36} />
              <Line 
                type="monotone" 
                dataKey="revenue" 
                name={`Revenue ${showWithTax ? '(After VAT)' : ''}`}
                stroke="#2ecc71" 
                activeDot={{ r: 8 }} 
                strokeWidth={2}
              />
            </LineChart>
          </ResponsiveContainer>
        </div>
      </div>
      
      {/* ROI Chart */}
      <div>
        <h2 style={{ 
          margin: '0 0 20px 0', 
          fontSize: '18px',
          fontWeight: 600,
          color: '#2c3e50'
        }}>
          ROI Over Time (%)
        </h2>
        
        <div style={{ width: '100%', height: 300 }}>
          <ResponsiveContainer>
            <LineChart
              data={chartData}
              margin={{ top: 20, right: 30, left: 20, bottom: 60 }}
            >
              <CartesianGrid strokeDasharray="3 3" />
              <XAxis 
                dataKey="date" 
                label={{ 
                  value: getXAxisLabel(), 
                  position: 'insideBottom', 
                  offset: -40 
                }}
                angle={-45}
                textAnchor="end"
                height={60}
              />
              <YAxis 
                label={{ 
                  value: 'ROI (%)', 
                  angle: -90, 
                  position: 'insideLeft',
                  style: { textAnchor: 'middle' }
                }}
              />
              <Tooltip formatter={(value) => typeof value === 'number' ? value.toFixed(2) + '%' : value} />
              <Legend verticalAlign="top" height={36} />
              <Line 
                type="monotone" 
                dataKey="roi" 
                name="ROI (%)" 
                stroke="#3498db" 
                activeDot={{ r: 8 }} 
                strokeWidth={2}
              />
            </LineChart>
          </ResponsiveContainer>
        </div>
      </div>
    </div>
  );
};

export default CostsGraphComponent;